import { User } from '@choco-admin/types';
import { Card, CardBody, Button, Chip } from '@heroui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

const getAccountTypeLabel = (user: User): React.ReactNode => {
  if (user.supplier) {
    return <>Distributor</>;
  }

  if (user.linkedBuyers && user.linkedBuyers.length > 0) {
    return <>Buyer User</>;
  }

  return <>User</>;
};

interface UserInfoCardsProps {
  user: User;
}

export const UserInfoCards: React.FC<UserInfoCardsProps> = ({ user }) => {
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {/* Linked Distributor Card */}
      <Card className="bg-content1 border-default-200">
        <CardBody className="text-center">
          <h3 className="font-semibold text-foreground mb-2">
            Linked Distributor
          </h3>
          {user.supplierId ? (
            <Button
              variant="light"
              size="sm"
              className="h-auto p-2 flex flex-col items-center justify-center"
              onPress={() => navigate(`/suppliers/${user.supplierId}`)}
            >
              <span className="text-sm text-primary hover:text-primary-600 cursor-pointer transition-colors underline decoration-1 underline-offset-2">
                {user.supplierName}
              </span>
            </Button>
          ) : (
            <p className="text-default-500">None</p>
          )}
        </CardBody>
      </Card>

      <Card className="bg-content1 border-default-200">
        <CardBody className="text-center">
          <h3 className="font-semibold text-foreground mb-2">Linked Buyers</h3>
          {user.linkedBuyers && user.linkedBuyers.length > 0 ? (
            <div className="space-y-1">
              {user.linkedBuyers.map((buyer) => (
                <Button
                  key={buyer.id}
                  variant="light"
                  size="sm"
                  className="h-auto p-1 min-h-0"
                  onPress={() => navigate(`/buyers/${buyer.id}`)}
                >
                  <span className="text-sm text-primary hover:text-primary-600 cursor-pointer transition-colors underline decoration-1 underline-offset-2">
                    {buyer.name}
                  </span>
                </Button>
              ))}
            </div>
          ) : (
            <p className="text-default-500">None</p>
          )}
        </CardBody>
      </Card>

      {/* Account Type Card */}
      <Card className="bg-content1 border-default-200">
        <CardBody className="text-center">
          <h3 className="font-semibold text-foreground mb-2">Account Type</h3>
          <div className="space-y-1">
            {user.isTest && (
              <Chip color="secondary" variant="flat" size="sm">
                Test User
              </Chip>
            )}

            <Chip color="primary" variant="flat" size="sm">
              {getAccountTypeLabel(user)}
            </Chip>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
